import React, { useContext, useEffect } from "react";
import { useImmer } from "use-immer";
import { useLocation } from "react-router-dom";
import { successMessage, errorMessage, infoMessage } from "../../../utils";
import {
  getTandaListAPi,
  updateTandaConnectionApi,
  getTandaVerifiedApi,
  disconnectTandaApi,
  getTandaDataStreamsApi,
  selectTandaDataStreamApi
} from "../api/tandaApi";
import { useActivityLog, usePermission, useTabs } from "../../../shared";
import { AppContext } from "../../../store";
import { TableStateContext } from "../../../store";
import { SITE_URL } from "../../../utils/apiConfig";
import qs from "qs";
import { useDebouncedCallback } from "use-debounce";

export const useTandaList = ({ isList }) => {
  const isEditable = usePermission("integration-modify");
  const { appState } = useContext(AppContext);
  const location = useLocation();

  // Define a constant for the page name used in TableStateContext
  const PAGE_NAME = "tanda";
  const {
    filterState: {
      params: { tanda },
    },
    initialState,
    filterStateDispatch,
  } = useContext(TableStateContext);

  const [state, setState] = useImmer({
    isBusy: false,
    isLoading: true,
    isSaveButton: false,
    isOpen: false,
    isOpenConnect: false,
    isErrorOpen: false,
    isModalType: "",
    errorMessages: "",
    external_store_id: "",
    selectedStore: null,
    tandaList: [],
    search: "",
    filterApplied: {
      status: {},
      searchText: null,
    },
    pagination: {
      pageIndex: 1,
      pageSize: 50,
      totalCount: 0,
    },
    sortBy: [],
    inputData: {
      application_id: "",
      client_secret: "",
    },
    isVerifying: false,
    disableApplyButton: true,
    isDataStreamOpen: false,
    isDataStreamLoading: false,
    dataStreams: [],
    dataStreamStoreId: null,
    dataStreamInput: {
      create_new: false,
      data_stream_id: "",
      name: "",
    },
  });

  let redirectURL = `${SITE_URL}tanda?id=${state.selectedStore?.id}`;

  // Add debounced search function
  const debounced = useDebouncedCallback((value) => {
    const { status } = state.filterApplied;
    filterStateDispatch({
      type: "SET_DATA",
      page: PAGE_NAME,
      data: { filterApplied: state.filterApplied },
    });
    getTandaList({
      search: value,
      pagination: {
        pageIndex: 1,
        pageSize: state.pagination.pageSize,
      },
      sortBy: state.sortBy,
      status,
    });
  }, 1000);

  // Handle Tanda verification
  const verifyTandaAuth = async (code, id) => {
    try {
      setState((draft) => {
        draft.isVerifying = true;
      });

      const payload = { code, store_id: id };
      const response = await getTandaVerifiedApi(payload);

      if (response.success) {
        successMessage(response.message || "Tanda authentication successful");
        // Refresh the list to show the updated connection status
        getTandaList({
          search: state.search,
          pagination: state.pagination,
          sortBy: state.sortBy,
          status: state.filterApplied.status,
        });
        // Prompt for a data stream right away, the store has none yet
        handleModal("dataStream", true, id);
      } else {
        errorMessage(response.message || "Tanda authentication failed");
      }

      setState((draft) => {
        draft.isVerifying = false;
      });
    } catch (error) {
      errorMessage(
        error.response?.data?.message || "Authentication verification failed",
      );
      setState((draft) => {
        draft.isVerifying = false;
      });
    }
  };

  // Check URL for Tanda auth parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get("code");
    const id = queryParams.get("id");

    if (code && id) {
      verifyTandaAuth(code, id);

      // Clean the URL after processing the parameters
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [location.search]);

  // Load stored filters and pagination from TableStateContext when component mounts
  useEffect(() => {
    if (isList) {
      const { status, searchText } = tanda?.filterApplied || {};
      const { pagination, sortBy } = tanda || {};

      setState((draft) => {
        draft.filterApplied = tanda?.filterApplied || state.filterApplied;
        draft.pagination = pagination || state.pagination;
        draft.sortBy = sortBy || state.sortBy;
        draft.search = searchText || state.search;
      });

      getTandaList({
        search: searchText,
        pagination: pagination || state.pagination,
        sortBy: sortBy || state.sortBy,
        status,
      });
    }
  }, [isList]);

  // Handle table pagination
  const gotoPage = (page) => {
    const { status } = state.filterApplied;
    const { sortBy } = state;

    getTandaList({
      search: state.search,
      pagination: {
        pageIndex: page,
        pageSize: state.pagination.pageSize,
      },
      sortBy,
      status,
    });

    filterStateDispatch({
      type: "UPDATE_PAGE_INDEX",
      page: PAGE_NAME,
      value: page,
    });

    setState((draft) => {
      draft.pagination.pageIndex = page;
    });
  };

  // Handle page size
  const setPageSize = (e) => {
    const newPageSize = +e.target.value;
    console.log("newPageSize", newPageSize);
    const { status } = state.filterApplied;
    const { sortBy } = state;

    getTandaList({
      search: state.search,
      pagination: {
        pageIndex: 1,
        pageSize: newPageSize,
      },
      sortBy,
      status,
    });

    filterStateDispatch({
      type: "UPDATE_PAGE_SIZE",
      page: PAGE_NAME,
      value: newPageSize,
    });

    setState((draft) => {
      draft.pagination.pageIndex = 1;
      draft.pagination.pageSize = newPageSize;
    });
  };

  // Handle sorting
  const handleSortBy = (sortBy) => {
    const { status } = state.filterApplied;

    getTandaList({
      search: state.search,
      pagination: state.pagination,
      sortBy,
      status,
    });

    filterStateDispatch({
      type: "UPDATE_SORT",
      page: PAGE_NAME,
      value: sortBy,
    });

    setState((draft) => {
      draft.sortBy = sortBy;
    });
  };

  // Handle search - updated to use debounced function
  const handleTableSearch = (event) => {
    const searchValue = event.target.value;
    setState((draft) => {
      draft.search = searchValue;
      draft.filterApplied.searchText = searchValue;
    });

    debounced(searchValue);
  };

  // Handle modals
  const handleModal = (type, state, data = null) => {
    if (type === "disconnect") {
      setState((draft) => {
        draft.isOpenConnect = state;
        if (data) {
          draft.selectedStore = data;
        }
      });
    } else if (type === "connect") {
      setState((draft) => {
        draft.isOpen = state;
        draft.inputData = {
          application_id: "",
          client_secret: "",
        };
        if (data) {
          draft.selectedStore = data;
        }
      });
    } else if (type === "dataStream") {
      setState((draft) => {
        draft.isDataStreamOpen = state;
        if (data) {
          draft.dataStreamStoreId = data;
        }
      });
      if (state) {
        getDataStreams(data);
      }
    }
  };

  // Handle error modal
  const isErrorModal = (state, message) => {
    setState((draft) => {
      draft.isErrorOpen = state;
      draft.errorMessages = message;
    });
  };

  // Handle input change
  const onChange = (e) => {
    setState((draft) => {
      draft.inputData[e.target.name] = e.target.value;
    });
  };

  // Fetch Tanda list from API
  const getTandaList = async ({ search, pagination, sortBy, status } = {}) => {
    try {
      setState((draft) => {
        draft.isLoading = true;
        draft.isBusy = true;
      });

      let params = {};

      // Add search parameter if it exists
      if (search) {
        params = { ...params, search };
      }

      // Add status if available
      if (status && Object.keys(status).length > 0) {
        params = { ...params, status: status.value };
      }

      // Add sorting parameters if available
      if (sortBy && sortBy.length > 0) {
        const sortByParams = sortBy.reduce(
          (obj, item) =>
            Object.assign(obj, { [item.id]: item.desc ? "desc" : "asc" }),
          {},
        );
        params = {
          ...params,
          sort: sortByParams,
        };
      }

      // Create query object with params
      const query = {
        params,
        paramsSerializer: (params) => qs.stringify(params),
      };

      const res = await getTandaListAPi(query, pagination || state.pagination);

      if (res.success) {
        setState((draft) => {
          draft.tandaList = res.data.stores || [];
          draft.pagination = res.data.pagination;
          draft.isLoading = false;
          draft.isBusy = false;
        });
      } else {
        errorMessage(res.message || "Failed to fetch Tanda list");
        setState((draft) => {
          draft.isLoading = false;
          draft.isBusy = false;
        });
      }
    } catch (error) {
      errorMessage(error.response?.data?.message || "Something went wrong");
      setState((draft) => {
        draft.isLoading = false;
        draft.isBusy = false;
      });
    }
  };

  // Handle store connection status update
  const handleStoreConnect = async (store, status) => {
    try {
      setState((draft) => {
        draft.isSaveButton = true;
      });

      const payload = {
        redirect_uri: `${SITE_URL}tanda?id=${state.selectedStore?.id}`,
        store_id: state.selectedStore.id,
        ...state.inputData,
      };

      const response = await updateTandaConnectionApi(payload);

      if (response.success) {
        if (response.data) {
          window.open(response.data, "_blank");
        }
        successMessage(
          response.message || "Connection status updated successfully",
        );
        getTandaList({
          search: state.search,
          pagination: state.pagination,
          sortBy: state.sortBy,
          status: state.filterApplied.status,
        });
        handleModal("connect", false);
      } else {
        errorMessage(response.message || "Failed to update connection status");
      }

      setState((draft) => {
        draft.isSaveButton = false;
      });
    } catch (error) {
      errorMessage(error.response?.data?.message || "Something went wrong");
      setState((draft) => {
        draft.isSaveButton = false;
      });
    }
  };

  // Handle store disconnection
  const handleStoreDisconnect = async () => {
    try {
      setState((draft) => {
        draft.isSaveButton = true;
      });

      const payload = {
        store_id: state.selectedStore.id,
      };

      const response = await disconnectTandaApi(payload);

      if (response.success) {
        successMessage(response.message || "Tanda successfully disconnected");
        getTandaList({
          search: state.search,
          pagination: state.pagination,
          sortBy: state.sortBy,
          status: state.filterApplied.status
        });
        handleModal("disconnect", false);
      } else {
        errorMessage(response.message || "Failed to disconnect Tanda");
      }

      setState((draft) => {
        draft.isSaveButton = false;
      });
    } catch (error) {
      errorMessage(error.response?.data?.message || "Something went wrong");
      setState((draft) => {
        draft.isSaveButton = false;
      });
    }
  };

  // Handle status filter
  const handleStatusFilter = (e) => {
    const status = e.target.value;
    setState((draft) => {
      draft.filterApplied.status = status ? {
        value: status,
        label: status === "1" ? "Connected" : "Disconnected",
        type: "Status",
      } : {};
      draft.disableApplyButton = false;
    });

    getTandaList({
      search: state.search,
      pagination: {
        pageIndex: 1,
        pageSize: state.pagination.pageSize
      },
      sortBy: state.sortBy,
      status: status ? { value: status } : {}
    });
    
    filterStateDispatch({
      type: "SET_DATA",
      page: PAGE_NAME,
      data: { 
        filterApplied: {
          ...state.filterApplied,
          status: status ? {
            value: status,
            label: status === "1" ? "Connected" : "Disconnected",
            type: "Status",
          } : {}
        }
      },
    });
  };

  // Reset filters
  const resetFilters = () => { 
    setState((draft) => {
      draft.filterApplied.status = {};
      draft.search = "";
      draft.filterApplied.searchText = "";
    });

    getTandaList({
      pagination: {
        pageIndex: 1,
        pageSize: state.pagination.pageSize
      },
      sortBy: state.sortBy
    });
    
    filterStateDispatch({
      type: "CLEAR_FILTERS",
      page: PAGE_NAME,
      value: initialState.params.tanda?.filterApplied || {
        status: {},
        searchText: "",
      },
    });
  };

  // Fetch the account's existing Tanda data streams
  const getDataStreams = async (storeId) => {
    try {
      setState((draft) => {
        draft.isDataStreamLoading = true;
      });

      const response = await getTandaDataStreamsApi({ store_id: storeId });

      if (response.success) {
        const { data_streams, selected_data_stream_id } = response.data || {};
        const streams = data_streams || [];

        setState((draft) => {
          draft.dataStreams = streams;
          draft.dataStreamInput = {
            create_new: !streams.length,
            data_stream_id: selected_data_stream_id ?? "",
            name: "",
          };
          draft.isDataStreamLoading = false;
        });
      } else {
        errorMessage(response.message || "Failed to fetch data streams");
        setState((draft) => {
          draft.isDataStreamLoading = false;
        });
      }
    } catch (error) {
      errorMessage(error.response?.data?.message || "Something went wrong");
      setState((draft) => {
        draft.isDataStreamLoading = false;
      });
    }
  };

  const onChangeDataStream = (name, value) => {
    setState((draft) => {
      draft.dataStreamInput[name] = value;
    });
  };

  // Store the chosen data stream, or create a new one with the given name
  const handleDataStreamSelect = async () => {
    try {
      setState((draft) => {
        draft.isSaveButton = true;
      });

      const { create_new, data_stream_id, name } = state.dataStreamInput;
      const payload = {
        store_id: state.dataStreamStoreId,
        create_new,
        data_stream_id: create_new ? null : data_stream_id,
        name: create_new ? name : null,
      };

      const response = await selectTandaDataStreamApi(payload);

      if (response.success) {
        successMessage(response.message || "Data stream selected successfully");
        handleModal("dataStream", false);
      } else {
        errorMessage(response.message || "Failed to select data stream");
      }

      setState((draft) => {
        draft.isSaveButton = false;
      });
    } catch (error) {
      errorMessage(error.response?.data?.message || "Something went wrong");
      setState((draft) => {
        draft.isSaveButton = false;
      });
    }
  };

  return {
    state,
    isEditable,
    redirectURL,
    handleTableSearch,
    gotoPage,
    setPageSize,
    handleSortBy,
    handleModal,
    handleStoreConnect,
    handleStoreDisconnect,
    isErrorModal,
    onChange,
    handleStatusFilter,
    resetFilters,
    onChangeDataStream,
    handleDataStreamSelect,
  };
};
